import { Badge, Box, Flex, IconButton } from '@chakra-ui/react';
import { ReactComponent as ImageIcon } from 'assets/icons/folderImage.svg';
import { memo, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { areEqual } from 'react-window';

import { THUMBNAIL_HEIGHT, THUMBNAIL_WIDTH } from '../../InfiniteGrid/InfiniteGrid.config';
import { HandleClickUserImage, UploadImagesData, UserImageCellProps } from '../UploadMenu.helper';
import { UserImage } from '../UserImage';
import { IMAGE_COLUMNS } from './Folders.helper';
import { ImageDragDropWrapper } from './ImageDragDropWrapper';
import { SelectUploadImageHoverIcon } from './SelectUploadImageHoverIcon';
import { SelectUploadImageIcon } from './SelectUploadImageIcon';

interface FolderImageData {
  uploadImages: UploadImagesData;
  setSelectedImages: (mediaId: string) => void;
  selectedImages: string[];
  disableDragDrop?: boolean;
}

const DEFAULT_STYLE = {
  buttonBorder: '1px solid var(--vg-colors-upgrade-blue-700)',
  buttonHover: { opacity: 0.7 },
  boxShadow: { boxShadow: 'none' },
};

const IMAGE_OFFSET = 3;

export const FolderImageCell = memo(
  ({ columnIndex, rowIndex, style, data }: UserImageCellProps<FolderImageData>, ref) => {
    const imageRef = useRef<HTMLImageElement>(null);
    const cellIdx = rowIndex * IMAGE_COLUMNS + columnIndex;
    const { uploadImages, setSelectedImages } = data;
    const imageData = uploadImages![cellIdx];

    const { t } = useTranslation('editor_widget_menu', { useSuspense: false });

    const onClickUserImage: HandleClickUserImage = useCallback(() => {
      if (!!imageData.mediaId) {
        setSelectedImages(imageData.mediaId);
      }
    }, [imageData?.mediaId, setSelectedImages]);

    const isSelected = data.selectedImages?.includes(imageData?.mediaId);

    return (
      imageData && (
        <Flex data-group style={style}>
          <ImageDragDropWrapper
            isDraggable={isSelected}
            dragImage={
              <Flex position='relative' m={2}>
                <IconButton variant='ghost-with-active' aria-label='selected images' isActive icon={<ImageIcon />} />
                <Badge
                  variant='solid'
                  colorScheme='red'
                  position='absolute'
                  top={-2}
                  right={-2}
                  borderRadius='full'
                  minW={5}
                  minH={5}
                  display='flex'
                  alignItems='center'
                  justifyContent='center'
                >
                  {data.selectedImages.length}
                </Badge>
              </Flex>
            }
            dragImageOffset={{ x: THUMBNAIL_WIDTH / IMAGE_OFFSET, y: THUMBNAIL_HEIGHT / IMAGE_OFFSET }}
          >
            <IconButton
              aria-label={t('sideBar.widget_menu_upload.userImageLabel')}
              variant='icon-btn-side-menu'
              h={style.height}
              w={style.width}
              onClick={onClickUserImage}
              data-testid='folder-insert-user-image'
              _focusWithin={DEFAULT_STYLE.boxShadow}
              _hover={DEFAULT_STYLE.boxShadow}
              role='group'
              p='2'
              borderRadius='base'
            >
              <Box
                border={isSelected ? DEFAULT_STYLE.buttonBorder : '1px solid white'}
                borderRadius='base'
                position='relative'
                _groupHover={{
                  border: DEFAULT_STYLE.buttonBorder,
                }}
                _groupFocusVisible={{
                  border: DEFAULT_STYLE.buttonBorder,
                  boxShadow: 'var(--vg-shadows-outline)',
                }}
              >
                <UserImage
                  id={imageData.mediaId}
                  idx={imageData.idx}
                  url={imageData.url}
                  imageRef={imageRef}
                  imageProps={{
                    _groupHover: DEFAULT_STYLE.buttonHover,
                    _groupFocusVisible: DEFAULT_STYLE.buttonHover,
                    borderRadius: 'base',
                  }}
                />
                <SelectUploadImageHoverIcon />
                <SelectUploadImageIcon isSelected={isSelected} />
              </Box>
            </IconButton>
          </ImageDragDropWrapper>
        </Flex>
      )
    );
  },
  areEqual,
);
FolderImageCell.displayName = 'UserImageCell';
